import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import { AnnouncedTeam } from 'modules/teams/domain/interfaces/announced-team';
import { TEAM_DEFAULT_LOGO } from 'shared/constants/links';
import { UDEntityRow } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';

interface TeamApplicationRowProps {
  team: AnnouncedTeam;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
  /**
   * Лимит заявки турнира (`applicationMaxPlayers`) — знаменатель «заявлено N из M».
   * Считает его экран: строка не знает `championshipId`, а лимит у всех строк один.
   */
  maxPlayers: number;
}

const STATUS_KEY: Record<AnnouncedTeamStatus, string> = {
  [AnnouncedTeamStatus.ANNOUNCED]: 'pending',
  [AnnouncedTeamStatus.PENDING]: 'pending',
  [AnnouncedTeamStatus.ACCEPTED]: 'accepted',
  [AnnouncedTeamStatus.DECLINED]: 'declined',
};

/**
 * Строка списка заявок v2: логотип, название, статус и разбор заявок команды.
 *
 * Главное отличие от карточки старого флоу (`championship-teams/championship-team-card.tsx`) —
 * здесь нет кнопок «Принять»/«Отклонить». Решение по команде принимается внутри её заявки,
 * где видно, кого именно заявляют; в списке остаётся только переход.
 */
const TeamApplicationRow = ({ team, index, maxPlayers }: TeamApplicationRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const open = useCallback(() => navigate(String(team.id)), [navigate, team.id]);

  const subtitle = useMemo(() => {
    const parts = [t(`applications.v2.teamStatus.${STATUS_KEY[team.status]}`)];

    if (team.applications.length === 0) {
      // Команда без единой заявки приходит из `championship { teams }` (фаза 7): состав у неё
      // может быть, просто его ещё не заявляли — это не то же, что команда без игроков вовсе.
      parts.push(
        team.membersCount
          ? t('applications.v2.teamNoApplications', { members: team.membersCount })
          : t('applications.v2.teamNoPlayers'),
      );

      return parts.join(' · ');
    }

    const countBy = (status: ApplicationStatus) => team.applications.filter((application) => application.status === status).length;

    const accepted = countBy(ApplicationStatus.ACCEPTED);
    const declined = countBy(ApplicationStatus.DECLINED);
    const pending = countBy(ApplicationStatus.PENDING);

    // Знаменатель — лимит турнира, а не число заявок: «заявлено 3 из 87» ничего не говорит
    // о том, сколько игроков вообще можно заявить.
    parts.push(t('applications.v2.teamAccepted', { accepted, limit: maxPlayers }));

    // Нули не печатаем: у команды без отклонённых хвост «отклонено 0» только шумит.
    if (declined > 0) parts.push(t('applications.v2.teamDeclined', { declined }));
    if (pending > 0) parts.push(t('applications.v2.teamPending', { pending }));
    if (team.membersCount) {
      parts.push(t('applications.v2.teamReviewed', { reviewed: accepted + declined, members: team.membersCount }));
    }

    return parts.join(' · ');
  }, [t, team.applications, team.status, team.membersCount, maxPlayers]);

  const title = team.shortName ? `${team.name} (${team.shortName})` : team.name;

  return (
    <UDEntityRow
      data-testid="team-application-row"
      index={index}
      avatar={team.logo?.url || TEAM_DEFAULT_LOGO}
      title={title}
      subtitle={subtitle}
      onClick={open}
      actions={
        <UDButton variant="secondary" onClick={open}>
          {t('applications.v2.openTeamButton')}
        </UDButton>
      }
    />
  );
};

export default React.memo(TeamApplicationRow);
